/* eslint-disable string-to-lingui/missing-lingui-transformation */
import { t } from '@lingui/macro'
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation'
import PersonIcon from '@mui/icons-material/Person'
import { Button, CircularProgress, FormControlLabel, MenuItem, Switch, TextField, Typography } from '@mui/material'
import Tooltip from '@mui/material/Tooltip'
import { OpenAPI } from '@om1/aspen-api/codegen/client'
import { PatientIndexDTO } from '@om1/aspen-api/codegen/client/models/PatientIndexDTO'
import { usePatientManagers } from '@om1/aspen-api/codegen/hooks/usePatientManagers'
import { usePatients } from '@om1/aspen-api/codegen/hooks/usePatients'
import OmniDataGridTable, { ProColumns } from '@om1/platform-components/OmniDataGridTable'
import DOMPurify from 'dompurify'
import { Download } from 'lucide-react'
import { useSnackbar } from 'notistack'
import React, { useCallback, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { CopyableIdentifier } from '../components/CopyableIdentifier'
import { FilterIconButton } from '../components/FilterIconButton'
import { DrawerComponent } from '../components/GlobalDrawer'
import LogAttemptAction from '../components/LogAttemptAction'
import { PatientManagerDropdown } from '../components/PatientManagerDropdown'
import PatientManagerFilter from '../components/PatientManagerFilter'
import { StableSearchInput } from '../components/StableSearchInput'
import { getCioxListStatusOptions, getCioxStatusLabel } from '../utils/ciox-status'
import RequestStatusIcon from './RequestStatusIcon'

/**
 * Sanitizes text content to prevent XSS attacks using DOMPurify
 * @param text - The text to sanitize
 * @returns Sanitized text safe for display
 */
const sanitizeText = (text: string | undefined | null): string => {
    if (!text) return ''
    return DOMPurify.sanitize(String(text), { ALLOWED_TAGS: [] })
}

type PatientWithGreenlightId = {
    greenlightId?: string | null
    // APP-34 read-side re-posture: queue-mode patients have greenlightId NULL by design but
    // are enrolled (origin owns the id). Drive the "enrolled" icon off greenlightEnrolled so
    // they no longer read as un-enrolled. Fields optional for backward-compatible rendering.
    greenlightEnrolled?: boolean
}

const isGreenlightEnrolled = (patient: PatientWithGreenlightId): boolean => patient.greenlightEnrolled ?? patient.greenlightId != null

export const PatientIconResolver = (patient: PatientWithGreenlightId) => {
    const enrolled = isGreenlightEnrolled(patient)
    // Show the raw vendor id in the tooltip when present (vendor-path patients); otherwise
    // convey enrollment status (queue-mode patients have no patient-level id).
    const tooltip = patient.greenlightId ? patient.greenlightId : enrolled ? t`Enrolled in Greenlight` : t`No Greenlight ID`
    return (
        <Tooltip title={tooltip}>
            <span className='flex items-center'>
                {enrolled ? <MedicalInformationIcon className='text-green-800' /> : <PersonIcon className='text-gray-400' />}
            </span>
        </Tooltip>
    )
}

const PatientsPage: React.FC = () => {
    const history = useHistory()
    const [selectedPatientManager, setSelectedPatientManager] = useState<string | null>(null)
    const [hasFailedRequests, setHasFailedRequests] = useState<boolean | null>(null)
    const [cioxStatus, setCioxStatus] = useState<string | null>(null)
    const [isDrawerOpen, setIsDrawerOpen] = useState(false)
    const [selectedPatientIds, setSelectedPatientIds] = useState<string[]>([])

    const patientsData = usePatients(1, 10, 'first_name:asc', {})

    // Update filters when dependencies change
    React.useEffect(() => {
        const newFilters: Record<string, any> = {}

        if (selectedPatientManager) {
            newFilters.patientManagerId = selectedPatientManager
        }
        if (hasFailedRequests !== null) {
            newFilters.hasFailedRequests = hasFailedRequests
        }
        if (cioxStatus) {
            newFilters.cioxStatus = cioxStatus
        }

        patientsData.setFilters(newFilters)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPatientManager, hasFailedRequests, cioxStatus])

    // Check if any filters are active
    const hasActiveFilters = useMemo(() => {
        return Boolean(
            selectedPatientManager !== null ||
                hasFailedRequests === true ||
                cioxStatus !== null ||
                (patientsData.search && patientsData.search.length > 0)
        )
    }, [selectedPatientManager, hasFailedRequests, cioxStatus, patientsData.search])

    // CSV Export
    const { enqueueSnackbar } = useSnackbar()
    const [isExporting, setIsExporting] = useState(false)

    const handleExportCsv = useCallback(async () => {
        setIsExporting(true)
        try {
            const params = new URLSearchParams()
            const filters = patientsData.filters || {}

            if (filters.patientManagerId) params.append('patient_manager_id', filters.patientManagerId)
            if (filters.carePartnerId) params.append('care_partner_id', filters.carePartnerId)
            // Every active filter has to go, or the download does not match the screen.
            if (filters.cioxStatus) params.append('ciox_status', filters.cioxStatus)
            if (filters.hasFailedRequests !== undefined) params.append('has_failed_requests', String(filters.hasFailedRequests))
            if (patientsData.search) params.append('search', patientsData.search)
            if (patientsData.sort) params.append('sort', patientsData.sort)

            const queryString = params.toString()
            const url = `${OpenAPI.BASE}/aspen/patients/export${queryString ? `?${queryString}` : ''}`

            const token = typeof OpenAPI.TOKEN === 'string' ? OpenAPI.TOKEN : await OpenAPI.TOKEN?.({ method: 'GET', url })
            const response = await fetch(url, {
                headers: {
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                }
            })

            if (!response.ok) {
                throw new Error(`Export failed: ${response.statusText}`)
            }

            const blob = await response.blob()
            const contentDisposition = response.headers.get('content-disposition')
            const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/)
            const filename = filenameMatch?.[1] || `patient_export_${new Date().toISOString().slice(0, 10)}.csv`

            const a = document.createElement('a')
            a.href = URL.createObjectURL(blob)
            a.download = filename
            a.click()
            URL.revokeObjectURL(a.href)

            enqueueSnackbar(t`CSV export downloaded successfully`, { variant: 'success' })
        } catch (error) {
            console.error('CSV export failed:', error)
            enqueueSnackbar(t`Failed to export CSV. Please try again.`, { variant: 'error' })
        } finally {
            setIsExporting(false)
        }
    }, [patientsData.filters, patientsData.search, patientsData.sort, enqueueSnackbar])

    // Memoize params to prevent infinite re-renders
    const patientManagersParams = React.useMemo(
        () => ({
            page: 1,
            limit: 100,
            active: true,
            sort: 'name:asc'
        }),
        []
    )

    // Fetch patient managers once at page level to avoid duplicate calls from each dropdown
    const { patientManagers: fetchedManagers, loading: managersLoading, error: managersError } = usePatientManagers(patientManagersParams)
    // Sanitize patient manager data to prevent XSS attacks before passing to components
    const patientManagers = React.useMemo(() => {
        return fetchedManagers.map((manager) => ({
            ...manager,
            name: sanitizeText(manager.name),
            email: sanitizeText(manager.email)
        }))
    }, [fetchedManagers])

    // Render Patients view with OmniDataGridTable

    // Define ProColumns for the patients data grid
    const proTablePatientColumns: ProColumns<PatientIndexDTO>[] = [
        {
            title: t`Greenlight ID`,
            dataIndex: 'greenlightId',
            key: 'greenlightId',
            width: 80,
            fixed: 'left',
            render: (_, patient) => <PatientIconResolver {...patient} />
        },
        {
            title: t`Identifier`,
            dataIndex: 'friendlyId',
            key: 'friendlyId',
            width: 150,
            render: (_: unknown, patient: PatientIndexDTO) => <CopyableIdentifier identifier={patient.friendlyId} />
        },
        {
            title: t`Crucial Subject ID`,
            dataIndex: 'externalId',
            key: 'externalId',
            width: 170,
            sorter: true,
            render: (_, patient) => patient.externalId || '-'
        },
        {
            title: t`First Name`,
            dataIndex: 'firstName',
            key: 'firstName',
            width: 150,
            sorter: true,
            render: (text, patient) => (
                <div className='flex flex-col gap-1'>
                    <button
                        type='button'
                        className='font-medium text-primary hover:underline'
                        onClick={() => history.push(`/patients/${patient.id}`)}
                    >
                        {text}
                    </button>
                </div>
            )
        },
        {
            title: t`Last Name`,
            dataIndex: 'lastName',
            key: 'lastName',
            width: 150,
            sorter: true,
            render: (text, patient) => (
                <button type='button' className='font-medium text-primary hover:underline' onClick={() => history.push(`/patients/${patient.id}`)}>
                    {text}
                </button>
            )
        },
        {
            title: t`Mobile Phone`,
            dataIndex: 'mobilePhone',
            key: 'mobilePhone',
            width: 140
        },
        {
            title: t`Created At`,
            dataIndex: 'createdDttm',
            key: 'createdDttm',
            width: 150,
            sorter: true,
            render: (text, patient) => {
                if (!patient.createdDttm) return '-'
                const date = new Date(patient.createdDttm)
                return date.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                })
            }
        },
        {
            title: t`Updated At`,
            dataIndex: 'updatedDttm',
            key: 'updatedDttm',
            width: 150,
            sorter: true,
            render: (text, patient) => {
                if (!patient.updatedDttm) return '-'
                const date = new Date(patient.updatedDttm)
                return date.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                })
            }
        },
        {
            title: t`Greenlight Request`,
            key: 'greenlightRequest',
            width: 150,
            align: 'center',
            render: (_, patient) => (
                <RequestStatusIcon
                    requestSummary={{
                        failedGreenlightRequestCount: patient.requestSummary?.failedGreenlightRequestCount ?? 0,
                        needsAttentionGreenlightRequestCount: patient.requestSummary?.needsAttentionGreenlightRequestCount ?? 0,
                        successGreenlightRequestCount: patient.requestSummary?.successGreenlightRequestCount ?? 0,
                        inProgressGreenlightRequestCount: patient.requestSummary?.inProgressGreenlightRequestCount ?? 0
                    }}
                />
            )
        },
        {
            title: t`CIOX Request`,
            dataIndex: 'cioxStatus',
            key: 'cioxStatus',
            width: 180,
            render: (_, patient) => <span>{getCioxStatusLabel(patient.cioxStatus)}</span>
        },
        {
            title: t`Patient Manager`,
            key: 'patientManager',
            width: 200,
            render: (_, patient) => (
                <PatientManagerDropdown
                    currentManagerId={patient.patientManagerId}
                    entityId={patient.id}
                    entityType='patient'
                    patientManagers={patientManagers}
                    loading={managersLoading}
                    onAssignmentComplete={() => patientsData.refetch?.()}
                />
            )
        },
        {
            title: t`Log Attempt`,
            key: 'logAttempt',
            width: 120,
            align: 'center',
            render: (_, patient) => (
                <LogAttemptAction entityName={`${patient.firstName} ${patient.lastName}`} entityId={patient.id} entityType='patient' />
            )
        },
        {
            title: t`Actions`,
            key: 'actions',
            width: 100,
            fixed: 'right',
            align: 'center',
            render: (_, patient) => (
                <Button variant='outlined' size='small' onClick={() => history.push(`/patients/${patient.id}`)}>
                    {t`View`}
                </Button>
            )
        }
    ]

    return (
        <div className='h-[90vh] flex flex-col'>
            {/* Header */}
            <div className='flex-shrink-0 p-6 pb-4 border-b'>
                <div className='flex justify-between items-center'>
                    <Typography variant='h4'>{t`Patient List`}</Typography>
                    <div className='flex items-center gap-4 flex-wrap'>
                        <Button
                            variant='outlined'
                            size='small'
                            startIcon={isExporting ? <CircularProgress size={16} /> : <Download size={16} />}
                            onClick={handleExportCsv}
                            disabled={isExporting}
                        >
                            {isExporting ? t`Exporting...` : t`Export CSV`}
                        </Button>
                        <FilterIconButton onClick={() => setIsDrawerOpen(true)} hasActiveFilters={hasActiveFilters} />
                    </div>
                </div>

                {/* Content Area */}
                <div className='flex-1 overflow-auto p-6 pt-4'>{/* Filters */}</div>

                {/* Bulk Actions Bar */}
                {selectedPatientIds.length > 0 && (
                    <div className='mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between'>
                        <Typography variant='body2' className='text-blue-800'>
                            {t`${selectedPatientIds.length} patient(s) selected`}
                        </Typography>
                        <div className='flex items-center gap-3'>
                            <PatientManagerDropdown
                                selectedEntityIds={selectedPatientIds}
                                entityType='patient'
                                patientManagers={patientManagers}
                                loading={managersLoading}
                                onAssignmentComplete={() => {
                                    patientsData.refetch?.()
                                    setSelectedPatientIds([])
                                }}
                                variant='button'
                                bulkTriggerLabel={t`Assign Patient Manager`}
                            />
                        </div>
                    </div>
                )}

                {/* Data Grid */}
                <div className='pb-12'>
                    <OmniDataGridTable<PatientIndexDTO>
                        dataSource={patientsData.response?.data || []}
                        columns={proTablePatientColumns}
                        loading={patientsData.loading}
                        enableRowSelection={true}
                        onSelectionChange={(selectedRowKeys, selectedRows) => {
                            setSelectedPatientIds(selectedRowKeys as string[])
                        }}
                        currentPage={patientsData.page}
                        pageSize={patientsData.limit}
                        total={patientsData.response?.meta?.totalItems || 0}
                        onPaginationChange={(page, pageSize) => {
                            patientsData.setPage(page)
                            patientsData.setLimit(pageSize)
                        }}
                        onSortChange={(sortString) => {
                            patientsData.setSort(sortString ?? '')
                        }}
                        rowKey='id'
                        enableColumnStatePersistence={true}
                        storageKey='patients-list'
                    />
                </div>
            </div>

            {/* Global Drawer for Filters */}
            <DrawerComponent open={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} title={t`Filters`} width={420}>
                <div className='flex flex-col gap-4 p-4'>
                    {/* Row 1: Patient Manager Filter */}
                    <div className='w-full'>
                        <Typography variant='subtitle2' className='font-semibold mb-2'>
                            {t`Patient Manager`}
                        </Typography>
                        <PatientManagerFilter
                            value={selectedPatientManager}
                            onChange={(id: string | null) => {
                                setSelectedPatientManager(id)
                            }}
                            size='small'
                            showAllOption={true}
                            showActiveOnly={true}
                            patientManagers={patientManagers}
                            loading={managersLoading}
                            error={managersError}
                        />
                    </div>

                    {/* Row 2: CIOX Request Status Filter */}
                    <div className='w-full'>
                        <Typography variant='subtitle2' className='font-semibold mb-2'>
                            {t`CIOX Request`}
                        </Typography>
                        <TextField
                            select
                            fullWidth
                            size='small'
                            value={cioxStatus ?? ''}
                            onChange={(event) => setCioxStatus(event.target.value || null)}
                        >
                            <MenuItem value=''>{t`All`}</MenuItem>
                            {getCioxListStatusOptions().map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </div>

                    {/* Row 3: Failed Requests Toggle */}
                    <div className='w-full'>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={hasFailedRequests === true}
                                    onChange={(event) => setHasFailedRequests(event.target.checked ? true : null)}
                                    size='small'
                                    color='error'
                                />
                            }
                            label={t`Show patients with failed Greenlight requests`}
                            sx={{
                                '& .MuiFormControlLabel-label': {
                                    fontSize: '0.875rem',
                                    color: hasFailedRequests === true ? 'error.main' : 'text.secondary'
                                }
                            }}
                        />
                    </div>

                    {/* Row 3: Search */}
                    <div className='w-full'>
                        <Typography variant='subtitle2' className='font-semibold mb-2'>
                            {t`Search`}
                        </Typography>
                        <StableSearchInput
                            onSearchChange={patientsData.setSearch}
                            initialValue={patientsData.search}
                            placeholder={t`Search by name, email, or identifier`}
                        />
                    </div>
                </div>
            </DrawerComponent>
        </div>
    )
}

export default PatientsPage
